import styled from "@emotion/styled";
import { useHeadsUpClick } from "@/components/heads-up/context/heads-up-context";
import { SmartNoteRow } from "@/components/heads-up/cards/common/smart-note-row";
import { NoteList, SmartCardSection } from "@/components/heads-up/cards/common/layout";
import { CollapsibleSectionHeader } from "@/components/heads-up/cards/common/collapsible-section-header";
import { useIncrementalReveal } from "@/components/heads-up/cards/common/use-incremental-reveal";
import { useCallback, useMemo } from "react";
import type { FC, MouseEvent } from "react";
import type { Maybe } from "@/domains/common/types";
import type { NoteListSmartCard } from "@/components/heads-up/heads-up.types";
import { SeeMoreButton } from "@/components/heads-up/cards/common/see-more-button";
import type { SmartNotePreviewInfo } from "@/components/heads-up/cards/common/smart-note-preview.types";
import type {
  HeadsUpCardRendererAnalytics,
  HeadsUpCardItemMeta,
} from "@/components/heads-up/cards/registry";
import { HeadsUpItemSource } from "@/components/heads-up/analytics";

type Props = {
  card: NoteListSmartCard;
  getNotePreviewInfo?: (noteId: string) => Maybe<SmartNotePreviewInfo>;
  analytics?: HeadsUpCardRendererAnalytics;
};

export const NoteListSmartCardRenderer: FC<Props> = ({ card, getNotePreviewInfo, analytics }) => {
  const clickHandlers = useHeadsUpClick();

  const sectionTitle = card.value.title;
  const baseItemSource = card.originated_from_find_more
    ? HeadsUpItemSource.FindMore
    : HeadsUpItemSource.Standard;

  const items = useMemo(
    () =>
      card.value.items.map((item, index) => ({
        id: item.note_id,
        primary: item.primary_label ?? "Untitled",
        secondary: item.secondary_label,
        positionIndex: index,
      })),
    [card]
  );

  const { visibleItems, hasMore, showAll, reset } = useIncrementalReveal({
    cardId: card.id,
    items,
    initialCount: 3,
    step: 5,
  });

  const buildAnalyticsMeta = useCallback(
    (item: (typeof items)[number]): HeadsUpCardItemMeta => ({
      itemKind: "note",
      itemId: item.id,
      itemTitle: item.primary,
      itemPositionIndex: item.positionIndex,
      itemSource: baseItemSource,
    }),
    [baseItemSource]
  );

  const handleNoteClick = useCallback(
    (item: (typeof items)[number]) => (event: MouseEvent<HTMLElement>) => {
      analytics?.onItemClick?.(buildAnalyticsMeta(item));
      const handler = clickHandlers?.noteClick(item.id);
      handler?.(event);
    },
    [analytics, buildAnalyticsMeta, clickHandlers]
  );

  return (
    <SmartCardSection>
      <CollapsibleSectionHeader title={sectionTitle} onCollapseChange={c => c && reset()}>
        <NoteList>
          {visibleItems.map(item => (
            <SmartNoteRow
              key={item.id}
              noteId={item.id}
              primary={item.primary}
              secondary={item.secondary || undefined}
              onClick={handleNoteClick(item)}
              onPreviewOpen={() => analytics?.onItemPreviewOpen?.(buildAnalyticsMeta(item))}
              notePreviewInfo={getNotePreviewInfo?.(item.id)}
            />
          ))}

          <SeeMoreButton hasMore={hasMore} onClick={showAll} />
        </NoteList>
      </CollapsibleSectionHeader>
    </SmartCardSection>
  );
};

export const SectionTitle = styled.span``;
